import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import {
  AutoGraph,
  Call as CallIcon,
  CheckCircle as CheckCircleIcon,
  GroupAdd as GroupAddIcon,
  Groups,
  HourglassEmpty as HourglassEmptyIcon,
  Insights,
  RecordVoiceOver as RecordVoiceOverIcon,
  SaveAlt,
  Search,
  Star,
  TrendingUp
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import axios from "axios";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import useDashboard from "../../hooks/useDashboard";
import { i18n } from "../../translate/i18n";
import ChartDonut from "./ChartDonut";
import { ChartsDate } from "./ChartsDate";
import { ChatsUser } from "./ChartsUser";

const LOGOUT_IN_PROGRESS_KEY = "soluwhats:logoutInProgress";

const StatCard = ({ title, value, icon, color, helper }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 0,
      overflow: "hidden",
      position: "relative",
      background: "transparent",
      boxShadow: "none",
      transition: "transform 0.22s ease",
      "&:hover": {
        transform: "translateY(-2px)"
      }
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: "text.secondary",
              fontSize: "0.68rem"
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              lineHeight: 1.1,
              mt: 1
            }}
          >
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.1, fontSize: "0.78rem" }}>
            {helper}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: color,
            color: "#fff",
            width: 48,
            height: 48,
            boxShadow: `0 10px 20px ${color}35`
          }}
        >
          <SvgIcon>{icon}</SvgIcon>
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const NpsMetricCard = ({ title, value, color }) => (
  <Card
    sx={{
      height: "100%",
      textAlign: "center",
      p: 2.25,
      borderRadius: 4,
      boxShadow: "0 12px 32px rgba(15,23,42,0.06)",
      border: "1px solid rgba(15,23,42,0.06)",
      background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))"
    }}
  >
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.14em", fontWeight: 800 }}>
      {title}
    </Typography>
    <Typography variant="h3" fontWeight="bold" sx={{ color, my: 1.5 }}>
      {value}%
    </Typography>
    <Box sx={{ height: 10, backgroundColor: "grey.200", borderRadius: 99, overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: `${value}%`, backgroundColor: color, borderRadius: 99 }} />
    </Box>
  </Card>
);

const surfaceSx = {
  p: { xs: 2, sm: 3 },
  borderRadius: 5,
  border: "1px solid rgba(15,23,42,0.06)",
  boxShadow: "0 18px 44px rgba(15,23,42,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))"
};

const formatMinutesLabel = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return "-";
  if (numericValue < 1) return `${Math.round(numericValue * 60)} seg`;
  if (numericValue < 60) return `${numericValue.toFixed(numericValue < 10 ? 1 : 0)} min`;
  const hours = Math.floor(numericValue / 60);
  const minutes = Math.round(numericValue % 60);
  return `${hours} h ${minutes} min`;
};

const levelPalette = {
  excellent: {
    bg: "linear-gradient(135deg, rgba(20,124,75,0.96) 0%, rgba(31,162,110,0.92) 100%)",
    chipBg: "rgba(255,255,255,0.14)"
  },
  warning: {
    bg: "linear-gradient(135deg, rgba(180,110,11,0.96) 0%, rgba(219,154,20,0.92) 100%)",
    chipBg: "rgba(255,255,255,0.14)"
  },
  critical: {
    bg: "linear-gradient(135deg, rgba(158,35,35,0.96) 0%, rgba(214,69,69,0.92) 100%)",
    chipBg: "rgba(255,255,255,0.14)"
  }
};

const Dashboard = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => 0);
  const [operationalPanel, setOperationalPanel] = useState({
    metrics: {},
    operationalAlerts: { summary: { staleTicketsCount: 0, unnamedContactsCount: 0, totalIssues: 0 } },
    openTicketsAssigned: 0,
    statusCard: {
      level: "excellent",
      title: "Buen ritmo de respuesta",
      message: ""
    },
    tips: [],
    topDelayedTickets: []
  });

  const { find, getOperationalPanel } = useDashboard();
  const { user } = useContext(AuthContext);
  const isOperationalUser = user?.profile === "user";

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
          date_to: new Date().toISOString().slice(0, 10)
        };
        const [data, operationalData] = await Promise.all([
          find(params),
          getOperationalPanel({})
        ]);
        if (!isMounted) {
          return;
        }
        setCounters(data.counters);
        if (isArray(data.attendants)) {
          setAttendants(data.attendants);
        }
        setOperationalPanel(operationalData || {});
      } catch (error) {
        const status = error?.response?.status;
        const requestCanceled =
          axios.isCancel(error) || error?.code === "ERR_CANCELED";
        const authSessionMissing =
          !localStorage.getItem("token") ||
          !localStorage.getItem("refreshToken");
        const isLoginScreen = window.location.pathname === "/login";
        const logoutInProgress = sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === "1";
        const ignoreError =
          requestCanceled ||
          authSessionMissing ||
          isLoginScreen ||
          logoutInProgress ||
          status === 401 ||
          status === 403;

        if (!ignoreError) {
          toast.error(i18n.t("dashboard.errors.loadData"));
        }
        console.error(error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [find, getOperationalPanel]);

  useEffect(() => {
    setActiveTab(user.profile === "user" ? 0 : 1);
  }, [user.profile]);

  const exportToExcel = () => {
    try {
      const table = document.getElementById("grid-attendants");
      const ws = XLSX.utils.table_to_sheet(table);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, i18n.t("dashboard.export.sheetName"));
      XLSX.writeFile(wb, i18n.t("dashboard.export.fileName"));
    } catch (_error) {
      toast.error(i18n.t("dashboard.errors.exportExcel"));
    }
  };

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const adminStatCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: PRIMARY_MAIN,
      helper: "Tickets abiertos en atencion en toda la empresa."
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: themeV5.palette.info.main,
      helper: "Tickets pendientes por asignar o responder."
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: themeV5.palette.success.main,
      helper: "Casos cerrados en el periodo seleccionado."
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: themeV5.palette.secondary.main,
      helper: "Conversaciones de grupo gestionadas."
    },
    {
      title: i18n.t("dashboard.cards.activeAttendants"),
      value: `${attendants.filter(item => item.online).length}/${attendants.length}`,
      icon: <RecordVoiceOverIcon />,
      color: themeV5.palette.error.main,
      helper: "Usuarios conectados frente al total operativo."
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: <GroupAddIcon />,
      color: themeV5.palette.warning.main,
      helper: "Contactos nuevos detectados en el periodo."
    }
  ];

  const operationalStatCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: PRIMARY_MAIN,
      helper: "Sus tickets abiertos o pendientes asignados en este momento."
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: themeV5.palette.info.main,
      helper: "Tickets suyos pendientes por responder."
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: themeV5.palette.success.main,
      helper: "Tickets cerrados por usted en el periodo."
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: themeV5.palette.secondary.main,
      helper: "Conversaciones de grupo que usted gestiono."
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.contacts || 0,
      icon: <GroupAddIcon />,
      color: themeV5.palette.warning.main,
      helper: "Contactos con historial atendido por usted."
    }
  ];

  const statCards = isOperationalUser ? operationalStatCards : adminStatCards;

  const dashboardTabs = isOperationalUser
    ? [
        { key: "operational", label: "Mi panel operativo" },
        { key: "performance", label: "Mi rendimiento" },
        { key: "nps", label: "Mis calificaciones" }
      ]
    : [
        { key: "operational", label: "Mi panel operativo" },
        { key: "performance", label: i18n.t("dashboard.tabs.performance") },
        { key: "nps", label: "NPS" },
        { key: "attendants", label: i18n.t("dashboard.tabs.attendants") }
      ];

  const npsData = {
    score: counters.npsScore || 0,
    promoters: counters.npsPromotersPerc || 0,
    passives: counters.npsPassivePerc || 0,
    detractors: counters.npsDetractorsPerc || 0,
    totalTickets: counters.tickets || 0,
    withRating: counters.withRating || 0,
    percRating: counters.percRating || 0
  };

  const npsColors = {
    [i18n.t("dashboard.assessments.prosecutors")]: "#2EA85A",
    [i18n.t("dashboard.assessments.detractors")]: "#F73A2C",
    [i18n.t("dashboard.assessments.neutral")]: "#F7EC2C"
  };

  const npsChartData = [
    { name: i18n.t("dashboard.assessments.prosecutors"), value: npsData.promoters },
    { name: i18n.t("dashboard.assessments.detractors"), value: npsData.detractors },
    { name: i18n.t("dashboard.assessments.neutral"), value: npsData.passives }
  ].sort((a, b) => a.name.localeCompare(b.name));

  const sortedNpsColors = npsChartData.map(item => npsColors[item.name]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 4,
        background: `
          radial-gradient(circle at top left, rgba(59,130,246,0.08), transparent 26%),
          radial-gradient(circle at top right, rgba(14,165,233,0.07), transparent 22%),
          linear-gradient(180deg, #f4f7fb 0%, #eef3f9 100%)
        `
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: PRIMARY_DARK }}>
            {i18n.t("dashboard.title") || "Dashboard"}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 34, height: 34, bgcolor: "rgba(15,23,42,0.06)", color: PRIMARY_DARK }}>
              <Insights sx={{ fontSize: 18 }} />
            </Avatar>
            <Avatar sx={{ width: 34, height: 34, bgcolor: "rgba(15,23,42,0.06)", color: PRIMARY_DARK }}>
              <TrendingUp sx={{ fontSize: 18 }} />
            </Avatar>
            <Avatar sx={{ width: 34, height: 34, bgcolor: "rgba(15,23,42,0.06)", color: PRIMARY_DARK }}>
              <Search sx={{ fontSize: 18 }} />
            </Avatar>
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            mb: 3.5,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 14px 34px rgba(15,23,42,0.10)",
            backgroundColor: "rgba(255,255,255,0.96)"
          }}
        >
          <Grid container>
            {statCards.map((card, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={2}
                key={card.title}
                sx={{
                  borderRight: {
                    lg: index < statCards.length - 1 ? "1px solid rgba(15,23,42,0.06)" : "none"
                  },
                  borderBottom: {
                    xs: index < statCards.length - 1 ? "1px solid rgba(15,23,42,0.06)" : "none",
                    lg: "none"
                  }
                }}
              >
                <StatCard {...card} />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4, display: "none" }}>
          {statCards.map(card => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={card.title}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 1,
            borderRadius: 5,
            bgcolor: "rgba(255,255,255,0.72)",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 10px 32px rgba(15,23,42,0.06)",
            backdropFilter: "blur(10px)"
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_event, nextValue) => setActiveTab(nextValue)}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.9rem",
                borderRadius: 3,
                minHeight: 46,
                color: "text.secondary",
                transition: "all .2s ease",
                "&.Mui-selected": {
                  color: PRIMARY_MAIN,
                  backgroundColor: "transparent"
                }
              },
              "& .MuiTabs-indicator": {
                backgroundColor: PRIMARY_MAIN,
                height: 3,
                borderRadius: 99
              }
            }}
          >
            {dashboardTabs.map(tab => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Paper>

        <Box>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    ...surfaceSx,
                    color: "#fff",
                    background: levelPalette[operationalPanel?.statusCard?.level || "excellent"]?.bg || levelPalette.excellent.bg,
                    overflow: "hidden",
                    position: "relative"
                  }}
                >
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: "0.14em", opacity: 0.86 }}>
                        PANEL OPERATIVO
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                        {operationalPanel?.statusCard?.title || "Buen ritmo de respuesta"}
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 1.5, opacity: 0.92, maxWidth: 720 }}>
                        {operationalPanel?.statusCard?.message || "Revise sus prioridades activas y mantenga continuidad en cada respuesta."}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                        <Chip
                          label={`Tickets activos: ${operationalPanel?.openTicketsAssigned || 0}`}
                          sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 700 }}
                        />
                        <Chip
                          label={`SLA 15 min: ${Number(operationalPanel?.metrics?.firstResponseWithin15mPct || 0)}%`}
                          sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 700 }}
                        />
                        <Chip
                          label={`Alertas: ${Number(operationalPanel?.operationalAlerts?.summary?.totalIssues || 0)}`}
                          sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 700 }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}>
                      <Typography variant="overline" sx={{ opacity: 0.8 }}>Resp. asesor</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {formatMinutesLabel(operationalPanel?.metrics?.avgAgentResponseMinutes)}
                      </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}>
                      <Typography variant="overline" sx={{ opacity: 0.8 }}>Resp. cliente</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {formatMinutesLabel(operationalPanel?.metrics?.avgClientResponseMinutes)}
                      </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="overline" color="text.secondary">Primera respuesta hoy</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatMinutesLabel(operationalPanel?.metrics?.avgFirstResponseMinutes)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Promedio de su primera respuesta enviada al cliente dentro del horario laboral.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="overline" color="text.secondary">Tickets medidos hoy</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                    {Number(operationalPanel?.metrics?.totalTickets || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Conversaciones del dia usadas para medir su tiempo de respuesta.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="overline" color="text.secondary">Tickets atrasados</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                    {Number(operationalPanel?.operationalAlerts?.summary?.staleTicketsCount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Casos abiertos o pendientes que ya piden reaccion.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="overline" color="text.secondary">Contactos sin nombre</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                    {Number(operationalPanel?.operationalAlerts?.summary?.unnamedContactsCount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Conviene nombrarlos para no perder contexto.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Recomendaciones del momento
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    {(operationalPanel?.tips || []).map((tip, index) => (
                      <Paper
                        key={`tip-${index}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          borderColor: "rgba(15,23,42,0.08)",
                          backgroundColor: "rgba(59,130,246,0.03)"
                        }}
                      >
                        <Typography variant="body2">{tip}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={surfaceSx}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Tickets con mayor demora hoy
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.25}>
                    {(operationalPanel?.topDelayedTickets || []).length ? operationalPanel.topDelayedTickets.map(ticket => (
                      <Paper
                        key={`delay-${ticket.ticketId}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          borderColor: "rgba(15,23,42,0.08)"
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {ticket.contactName} · Ticket #{ticket.ticketId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {ticket.queueName || "Sin cola"} · {ticket.status}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
                          <Chip label={`Asesor: ${formatMinutesLabel(ticket.avgAgentResponseMinutes)}`} size="small" />
                          <Chip label={`Primera: ${formatMinutesLabel(ticket.firstResponseMinutes)}`} size="small" />
                        </Stack>
                      </Paper>
                    )) : (
                      <Typography variant="body2" color="text.secondary">
                        Hoy no hay tickets medidos con demora relevante.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Paper elevation={0} sx={surfaceSx}>
              <ChartsDate isOperationalUser={isOperationalUser} />
            </Paper>
          )}

          {activeTab === 2 && (
            <Paper elevation={0} sx={surfaceSx}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(46,168,90,0.12)", color: "#2EA85A", mr: 2 }}>
                  <Star />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  {isOperationalUser ? "Mis calificaciones" : i18n.t("dashboard.tabs.assessments")}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                NPS mide la satisfaccion del cliente. Promotores son clientes muy satisfechos, neutros son clientes conformes y detractores son clientes inconformes.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 4,
                      border: "1px solid rgba(15,23,42,0.06)",
                      boxShadow: "0 12px 32px rgba(15,23,42,0.06)"
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      {i18n.t("dashboard.assessments.generalScore")}
                    </Typography>
                    <ChartDonut data={npsChartData} value={npsData.score} colors={sortedNpsColors} />
                  </Card>
                </Grid>

                <Grid item container xs={12} md={8} spacing={2}>
                  <Grid item xs={12} md={4}>
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.prosecutors")}
                      value={npsData.promoters}
                      color={npsColors[i18n.t("dashboard.assessments.prosecutors")]}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.neutral")}
                      value={npsData.passives}
                      color={npsColors[i18n.t("dashboard.assessments.neutral")]}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.detractors")}
                      value={npsData.detractors}
                      color={npsColors[i18n.t("dashboard.assessments.detractors")]}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 4,
                      borderColor: "rgba(15,23,42,0.08)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.04)"
                    }}
                  >
                    <Grid container spacing={2} textAlign="center">
                      {[
                        { title: i18n.t("dashboard.assessments.totalCalls"), value: npsData.totalTickets },
                        { title: i18n.t("dashboard.assessments.ratedCalls"), value: npsData.withRating },
                        { title: i18n.t("dashboard.assessments.evaluationIndex"), value: `${npsData.percRating.toFixed(1)}%` }
                      ].map((item, index) => (
                        <Grid item xs={12} sm={4} key={item.title} sx={{ p: 2, position: "relative" }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {item.title}
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" sx={{ color: PRIMARY_MAIN }}>
                            {item.value}
                          </Typography>
                          {index < 2 && (
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ position: "absolute", right: 0, top: "15%", height: "70%" }}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}

          {!isOperationalUser && activeTab === 3 && (
            <Paper elevation={0} sx={surfaceSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  {i18n.t("dashboard.tabs.attendants")}
                </Typography>
                <IconButton
                  onClick={exportToExcel}
                  size="small"
                  sx={{
                    backgroundColor: PRIMARY_MAIN,
                    color: PRIMARY_CONTRAST,
                    transition: "all .2s ease-in-out",
                    "&:hover": {
                      backgroundColor: PRIMARY_DARK,
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 18px rgba(0,0,0,.15)"
                    }
                  }}
                >
                  <SaveAlt />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <div id="grid-attendants">
                {attendants.length > 0 && <TableAttendantsStatus attendants={attendants} loading={loading} />}
              </div>
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {i18n.t("dashboard.charts.userPerformance")}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <ChatsUser isOperationalUser={isOperationalUser} />
              </Box>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
